/**
 * BloodTestBookingPage (Phase-2, Module 8)
 *
 * Officer books a blood test (test → lab → date → collection type → confirm),
 * then tracks the 7-status workflow and downloads the report once ready.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, FormControl, InputLabel, Select, IconButton, Tooltip, Chip, Typography, Divider, Stack, Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { WaterDrop as BloodIcon, Add as AddIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, OfficerSelector } from '../../components/common';
import { BloodTestApi, LaboratoryApi, toAbsoluteFileUrl } from '../../services/phase2Api';
import { COLLECTION_TYPES, labelFor } from '../../utils/phase2Constants';
import type { BloodTestBooking, BloodTestMaster, Laboratory } from '../../types/phase2';
import { useCurrentOfficer } from '../../hooks/useCurrentOfficer';
import { useNavigate, useLocation } from 'react-router-dom';

const BloodTestBookingPage: React.FC = () => {
  const { officerId } = useCurrentOfficer();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState<BloodTestBooking[]>([]);
  const [tests, setTests] = useState<BloodTestMaster[]>([]);
  const [labs, setLabs] = useState<Laboratory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<BloodTestBooking | null>(null);
  const [form, setForm] = useState({
    bloodTestId: '' as number | '', labId: '' as number | '', patientName: '',
    bookingDate: '', preferredTime: '', collectionType: 'LAB_VISIT', homeAddress: '', notes: '',
  });

  const load = useCallback(async () => {
    if (!officerId) return;
    setLoading(true); setError(null);
    try {
      const [page, t, l] = await Promise.all([
        BloodTestApi.listByOfficer(officerId),
        BloodTestApi.listAvailableTests(),
        LaboratoryApi.getActive(),
      ]);
      setRows(page?.content || []); setTests(t || []); setLabs(l || []);
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load blood tests'); }
    finally { setLoading(false); }
  }, [officerId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!officerId || !form.bloodTestId || !form.bookingDate || !form.collectionType) return;
    setSaving(true); setError(null);
    try {
      await BloodTestApi.book({
        officerId, bloodTestId: Number(form.bloodTestId),
        labId: form.labId ? Number(form.labId) : undefined,
        patientName: form.patientName || undefined, bookingDate: form.bookingDate,
        preferredTime: form.preferredTime || undefined, collectionType: form.collectionType,
        homeAddress: form.collectionType === 'HOME_COLLECTION' ? form.homeAddress : undefined,
        notes: form.notes || undefined,
      });
      setOpen(false);
      setForm({ bloodTestId: '', labId: '', patientName: '', bookingDate: '', preferredTime: '', collectionType: 'LAB_VISIT', homeAddress: '', notes: '' });
      load();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to book blood test'); }
    finally { setSaving(false); }
  };

  const columns: GridColDef[] = [
    { field: 'bookingNumber', headerName: 'Booking No.', width: 150 },
    { field: 'bloodTestName', headerName: 'Test', flex: 1.3, minWidth: 160 },
    { field: 'labName', headerName: 'Lab', width: 150, renderCell: (p) => p.value || '—' },
    { field: 'bookingDate', headerName: 'Date', width: 120 },
    { field: 'collectionType', headerName: 'Collection', width: 150, renderCell: (p) => <Chip size="small" label={labelFor(COLLECTION_TYPES, p.value)} variant="outlined" /> },
    { field: 'status', headerName: 'Status', width: 150, renderCell: (p) => <StatusBadge status={p.value} size="small" /> },
    {
      field: 'actions', headerName: 'Actions', width: 110, sortable: false,
      renderCell: (p) => (
        <Box>
          <Tooltip title="View"><IconButton size="small" onClick={() => setDetail(p.row)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <PageScaffold title="Blood Test Booking" subtitle="Book & track blood tests" icon={<BloodIcon />}
      actionLabel={officerId ? 'Book Test' : undefined} actionIcon={<AddIcon />} onAction={officerId ? () => setOpen(true) : undefined}
      loading={loading && rows.length === 0} error={error} onDismissError={() => setError(null)}>
      {!officerId && (
        <OfficerSelector onSelect={(id) => navigate(`${location.pathname}?officerId=${id}`)} />
      )}
      {officerId && (
      <>
      <Box sx={{ height: 520, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid rows={rows} columns={columns} getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Box>
      </>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Book Blood Test</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Blood Test</InputLabel>
              <Select label="Blood Test" value={form.bloodTestId} onChange={(e) => setForm({ ...form, bloodTestId: Number(e.target.value) })}>
                {tests.map((t) => <MenuItem key={t.id} value={t.id}>{t.testName}{t.fastingRequired ? ' (fasting)' : ''}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Lab</InputLabel>
              <Select label="Lab" value={form.labId} onChange={(e) => setForm({ ...form, labId: Number(e.target.value) })}>
                <MenuItem value="">Any</MenuItem>
                {labs.map((l) => <MenuItem key={l.id} value={l.id}>{l.labName}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Patient Name" value={form.patientName} onChange={(e) => setForm({ ...form, patientName: e.target.value })} fullWidth />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField type="date" label="Booking Date" InputLabelProps={{ shrink: true }} required value={form.bookingDate} onChange={(e) => setForm({ ...form, bookingDate: e.target.value })} fullWidth />
              <TextField label="Preferred Time" value={form.preferredTime} onChange={(e) => setForm({ ...form, preferredTime: e.target.value })} fullWidth />
            </Box>
            <FormControl fullWidth required>
              <InputLabel>Collection Type</InputLabel>
              <Select label="Collection Type" value={form.collectionType} onChange={(e) => setForm({ ...form, collectionType: e.target.value })}>
                {COLLECTION_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            {form.collectionType === 'HOME_COLLECTION' && (
              <Alert severity="info">Provide your home collection address below.</Alert>
            )}
            {form.collectionType === 'HOME_COLLECTION' && (
              <TextField label="Home Address" multiline minRows={2} value={form.homeAddress} onChange={(e) => setForm({ ...form, homeAddress: e.target.value })} fullWidth />
            )}
            <TextField label="Notes" multiline minRows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={!officerId || !form.bloodTestId || !form.bookingDate || saving}
            sx={{ textTransform: 'none', fontWeight: 600 }}>{saving ? 'Booking...' : 'Confirm Booking'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{detail?.bookingNumber}</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`Test: ${detail.bloodTestName}`} variant="outlined" />
                <Chip label={`Status: ${detail.statusDisplayName || detail.status}`} color="primary" />
                <Chip label={`Collection: ${labelFor(COLLECTION_TYPES, detail.collectionType)}`} variant="outlined" />
              </Box>
              <Typography variant="body2"><strong>Date:</strong> {detail.bookingDate} {detail.preferredTime}</Typography>
              {detail.labName && <Typography variant="body2"><strong>Lab:</strong> {detail.labName}</Typography>}
              {detail.homeAddress && <Typography variant="body2"><strong>Address:</strong> {detail.homeAddress}</Typography>}
              {detail.reportUrl && <Button size="small" variant="outlined" href={toAbsoluteFileUrl(detail.reportUrl)} target="_blank" rel="noopener noreferrer">Download Report</Button>}
              {detail.status !== 'COMPLETED' && detail.status !== 'CANCELLED' && (
                <Typography variant="caption" color="text.secondary">Track status here as the lab processes your sample.</Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setDetail(null)}>Close</Button></DialogActions>
      </Dialog>
    </PageScaffold>
  );
};

export default BloodTestBookingPage;